angular.module('dentalLinksServices')

// CbctStripService
// ---------------------------------------------------------------------------
// Wires the "CBCT scan" upload strip (the horizontal bar below the Attachments
// drop area) onto a controller scope. The strip is shared by create-referral
// (AttachmentsController) and view-referral (ViewReferralsController), so the
// state machine and selection handling live here rather than being duplicated.
//
// DCL-211: pick EITHER a folder OR a zip/single DICOM file, validate it
// client-side, and show a placeholder summary.
// DCL-212: upload the validated selection straight to S3.
// DCL-214: render the server-persisted scan (metadata card + download + delete)
// on page load, poll while it packages, and complete the loop after an in-session
// upload finalizes.
//
// State model exposed on the scope (DCL-211 + DCL-212 + DCL-214):
//   scope.cbct = {
//     enabled:    Boolean  // strip visible at all (false for guests)
//     state:      'empty' | 'validating' | 'selected' | 'uploading' | 'card'
//     chooserOpen: Boolean // the tiny folder/zip two-option chooser is open
//     error:      String|null
//     progress:   Number   // 0..100 overall upload percent (uploading state)
//     selection:  null | {           // set once validation passes
//       kind, sourceName, fileCount, totalSize, files, zipFile
//     }
//     scan:       null | {           // server-backed scan (DCL-214), in 'card' state
//       id, status, source_name, selection_kind, file_count, total_bytes,
//       created_at, author_name, download_url, can_delete
//     }
//     deleting:   Boolean  // a delete request is in flight (disables the trash)
//   }
// State flow:
//   empty -> validating -> selected -> uploading -> card (uploaded/processing)
//                      (automatic upload)          (finalize -> hydrate + poll)
//   uploading --Cancel--> empty  (aborts XHRs + server record)
//   card(uploaded|processing) --poll--> card(ready)  (Download appears)
//   card --Delete/Retry--> empty (server record removed, slot freed)
//   any --eligibility lost--> empty (DCL-523: silent drop, see below)
//   [page load] server scan present -> card (poll if not yet ready/failed)
//
// DCL-523: attaching needs Premium on EITHER practice on the referral, so the
// host supplies config.eligibilityFn (true / false / null-when-unsettled).
// scope.cbctCanAttach() drives the button-vs-upgrade-prompt choice, and a
// true -> false transition drops whatever is in the strip without a prompt.
// Methods on the scope:
//   scope.cbctToggleChooser(), scope.cbctCloseChooser()
//   scope.cbctPickFolder(), scope.cbctPickZip()   // trigger the hidden inputs
//   scope.cbctOnFilesSelected(fileList)           // validates, then starts upload
//   scope.cbctStartUpload()                       // selected -> uploading -> card
//   scope.cbctCancelUpload()                      // uploading -> empty (abort)
//   scope.cbctInitFromScan(scanJson)              // hydrate the card from server data
//   scope.cbctDelete()                            // confirm + delete the committed scan
//   scope.cbctRetryFailed()                       // clear a failed record -> button
//
// Upload needs the draft referral id: the host controller supplies
// scope.ensureDraftForUpload() (lazily creates the draft, same chain as regular
// attachments) and, for guest hosts, scope.draftUploadHeaders().
//
// Show/poll/delete need the referral id from the host:
//   scope.cbctReferralId()  -> current referral id or null (no polling until set)
// Delete permission comes from the server: the scan JSON carries a per-requester
// `can_delete` (true only for the uploading practice), which cbctCanDelete()
// reads to show/hide the trash.
//
// The two hidden <input> elements are addressed by id (unique per host page) so
// this service can .click() them without a DOM reference.
    .factory('CbctStripService', ['$q', '$http', '$interval', 'ConfirmDialog', '$rootScope', 'AUTH_EVENTS', 'API_ENDPOINT', 'Notification', 'Auth', 'CbctValidationService', 'CbctUploadService',
        function ($q, $http, $interval, ConfirmDialog, $rootScope, AUTH_EVENTS, API_ENDPOINT, Notification, Auth, CbctValidationService, CbctUploadService) {

            // Poll the show endpoint every 5s while a scan packages, giving up
            // after a generous ceiling so a stuck/never-arriving job doesn't poll
            // forever. A ~1.5 GB scan packages well within this window.
            var POLL_INTERVAL_MS = 5000;
            var POLL_MAX_MS = 15 * 60 * 1000; // 15 minutes

            // Statuses that still need polling (not yet a terminal ready/failed).
            var PENDING_STATUSES = ['pending', 'uploading', 'uploaded', 'processing'];

            function resetSelection(scope) {
                scope.cbct.state = 'empty';
                scope.cbct.error = null;
                scope.cbct.selection = null;
                scope.cbct.progress = 0;
                scope.cbct.uploadSession = null;
                scope.cbct.scan = null;
                scope.cbct.deleting = false;
            }

            // Map a CBCT upload failure to a user-facing message, or null for a
            // user-initiated cancel (which is handled elsewhere).
            //   - server rejections (create/presign) are $http errors carrying a
            //     { message } body, e.g. the DCL-215 monthly upload-cap rejection —
            //     surface that message verbatim.
            //   - mid-upload S3/network failures are plain Errors with .message —
            //     show a generic retry prompt (but treat 'cancelled' as a cancel).
            function cbctUploadErrorMessage(err) {
                if (!err) { return 'The CBCT scan upload failed. Please try again.'; }
                if (err.data && err.data.message) { return err.data.message; }
                if (err.message === 'cancelled') { return null; }
                return 'The CBCT scan upload failed. Please try again.';
            }

            // DCL-420: toast message for a scan the packaging worker rejected
            // (poll flips it to 'failed'). The server sends a short, already
            // user-safe `failure_reason` (e.g. "5 unrecognized files, starting
            // with asdf.dat") when the failure has actionable detail; fall back
            // to the generic copy when it doesn't (e.g. a transient S3 failure).
            function cbctPackagingFailureMessage(scan) {
                if (scan && scan.failure_reason) {
                    return 'CBCT scan upload failed: ' + scan.failure_reason;
                }
                return 'The CBCT scan upload failed. Please try again.';
            }

            // config: { folderInputId, fileInputId, enabled?, eligibilityFn? }
            function attach(scope, config) {
                config = config || {};
                var folderInputId = config.folderInputId;
                var fileInputId = config.fileInputId;

                var enabled = typeof config.enabled === 'boolean'
                    ? config.enabled
                    : !!Auth.get(); // guests have no auth_context cookie -> no strip

                // DCL-328/DCL-523: attaching a scan needs Premium on EITHER side of
                // the referral, so eligibility depends on the counterpart as well as
                // the current practice — which is why hosts supply it. The function
                // returns true (may attach), false (may not), or null when the
                // answer isn't settled yet: the create page's practice typeahead
                // blanks its selection on every keystroke, and a null there must not
                // be mistaken for "the user chose a Basic destination".
                //
                // The default covers hosts with nothing to add: the current
                // practice's own tier, from the server via the shared
                // Auth.current_user.practice.premium_access flag (the same signal
                // the Patient Portal badge uses). It loads asynchronously, so it is
                // read lazily on each digest.
                var eligibilityFn = typeof config.eligibilityFn === 'function'
                    ? config.eligibilityFn
                    : function () {
                        var u = Auth.current_user;
                        return !!(u && u.practice && u.practice.premium_access);
                    };

                scope.cbct = {
                    enabled: enabled,
                    state: 'empty',
                    chooserOpen: false,
                    error: null,
                    progress: 0,
                    selection: null,
                    uploadSession: null,
                    scan: null,
                    deleting: false
                };

                // True when a scan may be attached to this referral (DCL-523).
                // Anything short of a definite yes — including the unsettled null —
                // shows the upgrade prompt instead of the button.
                scope.cbctCanAttach = function () {
                    return eligibilityFn() === true;
                };

                // Open the shared upgrade modal (same one the global 402 handler
                // shows) so upgrading from the strip reuses the established flow,
                // including the Stripe checkout redirect.
                scope.cbctUpgrade = function () {
                    $rootScope.$broadcast(AUTH_EVENTS.paymentRequired, {feature: 'cbct', source: 'cbct'});
                };

                // --- polling lifecycle (DCL-214) ---------------------------------
                // A single $interval handle + a deadline so a stuck packaging job
                // doesn't poll forever. Cleared on terminal status, delete, and
                // $destroy.
                var pollHandle = null;
                var pollDeadline = 0;

                function stopPolling() {
                    if (pollHandle) {
                        $interval.cancel(pollHandle);
                        pollHandle = null;
                    }
                }

                // Fetch the latest scan JSON and re-hydrate the card. Stops polling
                // once the scan reaches a terminal status or the deadline passes.
                function pollOnce() {
                    var referralId = referralIdOrNull();
                    var scan = scope.cbct.scan;
                    if (!referralId || !scan || !scan.id) { stopPolling(); return; }
                    if (Date.now() > pollDeadline) { stopPolling(); return; }

                    var url = API_ENDPOINT + '/referrals/' + referralId + '/cbct/' + scan.id;
                    $http.get(url).then(function (resp) {
                        // The card may have been deleted/reset while the request
                        // was in flight — ignore a stale response.
                        if (scope.cbct.state !== 'card' || !scope.cbct.scan ||
                            scope.cbct.scan.id !== (resp.data && resp.data.id)) {
                            return;
                        }
                        var wasFailed = scope.cbct.scan.status === 'failed';
                        scope.cbct.scan = resp.data;
                        if (PENDING_STATUSES.indexOf(resp.data.status) === -1) {
                            stopPolling(); // ready or failed — done
                            // DCL-420: the packaging worker rejected the scan while
                            // the user watched it process — toast the reason (with
                            // the server's detail when it has one) so they know what
                            // to fix, matching the pre-upload validation toasts.
                            if (resp.data.status === 'failed' && !wasFailed) {
                                Notification.error(cbctPackagingFailureMessage(resp.data), {dontHideOnRequest: true});
                            }
                        }
                    }, function (err) {
                        // A 404 means the record is gone (deleted elsewhere / swept);
                        // drop the card. Transient errors just wait for the next tick.
                        if (err && err.status === 404) {
                            stopPolling();
                            resetSelection(scope);
                        }
                    });
                }

                function startPollingIfNeeded() {
                    var scan = scope.cbct.scan;
                    if (!scan || PENDING_STATUSES.indexOf(scan.status) === -1) { return; }
                    if (!referralIdOrNull()) { return; }
                    stopPolling();
                    pollDeadline = Date.now() + POLL_MAX_MS;
                    pollHandle = $interval(pollOnce, POLL_INTERVAL_MS);
                }

                function referralIdOrNull() {
                    if (typeof scope.cbctReferralId === 'function') {
                        return scope.cbctReferralId() || null;
                    }
                    return null;
                }

                // The button both opens and closes the chooser (DCL-410).
                scope.cbctToggleChooser = function () {
                    if (scope.cbct.chooserOpen) { scope.cbctCloseChooser(); return; }
                    if (scope.cbct.state === 'validating') { return; }
                    // No Premium on either side — send them to upgrade.
                    if (!scope.cbctCanAttach()) { scope.cbctUpgrade(); return; }
                    scope.cbct.chooserOpen = true;
                };

                scope.cbctCloseChooser = function () {
                    scope.cbct.chooserOpen = false;
                };

                // DCL-410: dismiss the chooser when the user clicks anywhere
                // outside it. Clicks inside .cbct-chooser-wrap (the button and the
                // two options) are handled by their own ng-clicks, so only outside
                // clicks close here. Capture phase so a stopPropagation elsewhere
                // on the page can't strand the chooser open.
                function insideChooserWrap(node) {
                    while (node) {
                        if (node.classList && node.classList.contains('cbct-chooser-wrap')) {
                            return true;
                        }
                        node = node.parentNode;
                    }
                    return false;
                }

                function onDocumentClick(event) {
                    if (!scope.cbct.chooserOpen) { return; }
                    if (insideChooserWrap(event.target)) { return; }
                    scope.$applyAsync(function () {
                        scope.cbct.chooserOpen = false;
                    });
                }

                document.addEventListener('click', onDocumentClick, true);

                function clickInput(id) {
                    var el = document.getElementById(id);
                    if (el) {
                        el.value = ''; // allow re-selecting the same folder/file
                        el.click();
                    }
                }

                scope.cbctPickFolder = function () {
                    scope.cbct.chooserOpen = false;
                    clickInput(folderInputId);
                };

                scope.cbctPickZip = function () {
                    scope.cbct.chooserOpen = false;
                    clickInput(fileInputId);
                };

                // Called from the hidden inputs' change handler. `fileList` is the
                // input's FileList. Runs validation and immediately starts uploading
                // a valid selection, or returns to 'empty' (with a Notification) on
                // failure. Returns a promise for testability.
                scope.cbctOnFilesSelected = function (fileList) {
                    if (!fileList || fileList.length === 0) {
                        return $q.resolve();
                    }
                    scope.cbct.state = 'validating';
                    scope.cbct.error = null;
                    return CbctValidationService.validate(fileList).then(function (summary) {
                        scope.cbct.selection = summary;
                        scope.cbct.state = 'selected';
                        return scope.cbctStartUpload();
                    }, function (err) {
                        var message = (err && err.message) || 'That selection is not a CBCT scan.';
                        scope.cbct.error = message;
                        resetSelection(scope);
                        Notification.error(message, {dontHideOnRequest: true});
                    });
                };

                // selected -> uploading -> uploaded. Lazily ensures the draft
                // referral exists (same chain regular attachments use), then
                // streams the validated selection straight to S3.
                scope.cbctStartUpload = function () {
                    if (scope.cbct.state !== 'selected' || !scope.cbct.selection) {
                        return $q.resolve();
                    }
                    // Eligibility can be revoked while a selection validates
                    // (DCL-523) — don't start an upload the server would reject.
                    if (!scope.cbctCanAttach()) {
                        resetSelection(scope);
                        return $q.resolve();
                    }
                    if (typeof scope.ensureDraftForUpload !== 'function') {
                        Notification.error('Could not start the CBCT upload yet. Please try again.');
                        return $q.reject('draft.not.ready');
                    }

                    scope.cbct.state = 'uploading';
                    scope.cbct.progress = 0;
                    scope.cbct.error = null;

                    return scope.ensureDraftForUpload().then(function (referralId) {
                        var session = CbctUploadService.start({
                            referralId: referralId,
                            selection: scope.cbct.selection,
                            headersFn: scope.draftUploadHeaders
                        });
                        scope.cbct.uploadSession = session;

                        return session.run(function (pct) {
                            scope.cbct.progress = pct;
                        }).then(function (finalized) {
                            scope.cbct.uploadSession = null;
                            scope.cbct.progress = 100;
                            // Seamlessly transition into the same server-backed card
                            // the page-load path uses, and start polling until the
                            // packaging worker takes it ready/failed. `finalized` is
                            // the finalize response (uploaded status); fall back to a
                            // minimal shape from the local selection if it's absent.
                            scope.cbctInitFromScan(finalized || {
                                id: session.cbctId,
                                status: 'uploaded',
                                source_name: scope.cbct.selection && scope.cbct.selection.sourceName,
                                selection_kind: scope.cbct.selection && scope.cbct.selection.kind,
                                file_count: scope.cbct.selection && scope.cbct.selection.fileCount,
                                total_bytes: scope.cbct.selection && scope.cbct.selection.totalSize,
                                // This session just uploaded the scan, so its
                                // practice may delete it (the server echoes the
                                // same flag once polling refreshes the card).
                                can_delete: true
                            });
                        }, function (err) {
                            // A user-initiated cancel resets to empty elsewhere; only
                            // surface genuine failures here.
                            if (scope.cbct.state === 'uploading') {
                                scope.cbct.uploadSession = null;
                                var message = cbctUploadErrorMessage(err);
                                resetSelection(scope);
                                if (message) {
                                    // Keep the friendly message visible in the strip too,
                                    // not just the toast (matches the regular queue path).
                                    scope.cbct.error = message;
                                    Notification.error(message, {dontHideOnRequest: true});
                                }
                            }
                        });
                    }, function () {
                        // Draft couldn't be created yet.
                        resetSelection(scope);
                        Notification.error('Could not start the CBCT upload yet. Please try again.');
                    });
                };

                // uploading -> empty. Aborts in-flight PUTs and the server record.
                scope.cbctCancelUpload = function () {
                    if (scope.cbct.state !== 'uploading') { return $q.resolve(); }
                    var session = scope.cbct.uploadSession;
                    resetSelection(scope); // flip UI back to empty immediately
                    if (session) { return session.cancel(); }
                    return $q.resolve();
                };

                // --- server-backed card (DCL-214) --------------------------------

                // Hydrate the strip's card from a server scan JSON (page load or
                // post-finalize). No-op for a missing/empty scan so callers can pass
                // referral.cbct_scan directly. Starts polling if not yet terminal.
                scope.cbctInitFromScan = function (scan) {
                    if (!scan || !scan.id) { return; }
                    scope.cbct.scan = scan;
                    scope.cbct.selection = null;
                    scope.cbct.error = null;
                    scope.cbct.deleting = false;
                    scope.cbct.state = 'card';
                    startPollingIfNeeded();
                };

                // Only the uploading practice may delete (mirrors the backend's
                // DELETE authorization). The server computes `can_delete` per
                // requester on every scan payload, so the trash is hidden unless
                // it says so.
                scope.cbctCanDelete = function () {
                    return !!(scope.cbct.scan && scope.cbct.scan.can_delete);
                };

                // Whether the card currently offers a Download (ready + url).
                scope.cbctCanDownload = function () {
                    var scan = scope.cbct.scan;
                    return !!(scan && scan.status === 'ready' && scan.download_url);
                };

                // --- expiry (DCL-329) --------------------------------------------
                // A ready scan expires 90 days after upload; the server sends the
                // computed `expires_at` while ready and `expired_at` once expired.

                // True once the scan has aged out (files removed, record retained).
                scope.cbctScanExpired = function () {
                    var scan = scope.cbct.scan;
                    return !!(scan && scan.status === 'expired');
                };

                // Whole days from now until the ready scan's expiry (rounded up).
                // Null when there's no ready scan / no expires_at. May be <= 0 if the
                // cron hasn't run yet the day it's due.
                scope.cbctDaysUntilExpiry = function () {
                    var scan = scope.cbct.scan;
                    if (!scan || scan.status !== 'ready' || !scan.expires_at) { return null; }
                    var ms = new Date(scan.expires_at).getTime() - Date.now();
                    return Math.ceil(ms / (24 * 60 * 60 * 1000));
                };

                // Show the urgency "(in N days)" hint only in the final stretch
                // (N <= 30), and only while it's still in the future.
                scope.cbctShowExpiryCountdown = function () {
                    var days = scope.cbctDaysUntilExpiry();
                    return days !== null && days <= 30 && days >= 0;
                };

                // Absolute download URL for the ready scan (server returns a
                // proxy-relative path; prefix API_ENDPOINT like the other filters).
                scope.cbctDownloadUrl = function () {
                    var scan = scope.cbct.scan;
                    return scan && scan.download_url ? API_ENDPOINT + scan.download_url : '';
                };

                // DCL-216: absolute URL for the CBCT slice thumbnail, or '' when
                // there isn't one (generation is best-effort/async — the card
                // falls back to the generic 3D icon). The server sends a
                // proxy-relative path keyed to thumbnails/<id>.jpg; append
                // is_thumb=true so the proxy serves from the thumbnails/ prefix,
                // mirroring the attachmentThumb directive for regular images.
                scope.cbctThumbnailUrl = function () {
                    var scan = scope.cbct.scan;
                    if (!scan || !scan.thumbnail_url) { return ''; }
                    return API_ENDPOINT + scan.thumbnail_url + '&is_thumb=true';
                };

                // Delete the committed scan (trash icon). Confirms like a regular
                // attachment delete, calls DELETE, then frees the strip to the
                // button. Originating side only.
                scope.cbctDelete = function () {
                    if (scope.cbct.state !== 'card' || !scope.cbctCanDelete()) { return $q.resolve(); }
                    return ConfirmDialog.open({
                        title: 'Delete this CBCT scan?',
                        message: 'The scan will be removed from this referral.',
                        confirmText: 'Delete scan'
                    }).then(function (confirmed) {
                        if (confirmed) {
                            return deleteScan();
                        }
                    });
                };

                // Clear a failed record so the button reappears (retry-from-scratch).
                // Same delete call — a failed scan is inactive but still a row.
                scope.cbctRetryFailed = function () {
                    if (scope.cbct.state !== 'card') { return $q.resolve(); }
                    return deleteScan();
                };

                // `silent` (DCL-523) is the eligibility drop: the user didn't ask
                // for this, so a failure gets no toast. The strip still returns to
                // the locked prompt, and the server drops the record itself on the
                // next autosave, so the two can't disagree for long.
                function deleteScan(silent) {
                    var referralId = referralIdOrNull();
                    var scan = scope.cbct.scan;
                    if (!referralId || !scan || !scan.id) {
                        resetSelection(scope);
                        return $q.resolve();
                    }
                    scope.cbct.deleting = true;
                    stopPolling();
                    var url = API_ENDPOINT + '/referrals/' + referralId + '/cbct/' + scan.id;
                    return $http.delete(url).then(function () {
                        resetSelection(scope); // back to the button
                    }, function (err) {
                        scope.cbct.deleting = false;
                        // 404 => already gone; treat as success and free the slot.
                        if (err && err.status === 404) {
                            resetSelection(scope);
                            return;
                        }
                        if (silent) {
                            resetSelection(scope);
                            return;
                        }
                        Notification.error('Could not delete the CBCT scan. Please try again.', {dontHideOnRequest: true});
                    });
                }

                // --- eligibility revoked (DCL-523) -------------------------------
                // CBCT unlocks when either practice on the referral is Premium, so
                // on the create page eligibility follows the destination: choosing a
                // Basic practice after a scan was attached takes the slot away
                // again. Drop the scan silently — no confirmation, no toast — and
                // fall back to the upgrade prompt.
                //
                // Only a definite true -> false transition drops anything. The
                // practice typeahead blanks its selection on every keystroke, which
                // the host reports as null (unsettled); treating that as "the user
                // chose a Basic destination" would destroy a scan mid-typing.
                function dropScanSilently() {
                    if (scope.cbct.state === 'uploading') {
                        var session = scope.cbct.uploadSession;
                        resetSelection(scope); // flip the UI back before the abort lands
                        if (session) { session.cancel(); }
                        return;
                    }
                    if (scope.cbct.state === 'card') {
                        // Only the uploading practice may delete. Someone else's scan
                        // stays visible (view/download is never gated — DCL-510).
                        if (scope.cbctCanDelete()) { deleteScan(true); }
                        return;
                    }
                    if (scope.cbct.state !== 'empty') {
                        resetSelection(scope); // validating/selected: nothing uploaded yet
                    }
                }

                scope.$watch(eligibilityFn, function (now, before) {
                    if (before === true && now === false) { dropScanSilently(); }
                });

                // Stop polling and release the document listener when the host
                // scope is torn down.
                scope.$on('$destroy', function () {
                    stopPolling();
                    document.removeEventListener('click', onDocumentClick, true);
                });

                return scope.cbct;
            }

            return {
                attach: attach
            };
        }]);
